import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { StyledEngineProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import store from "./modules/questionnaire/store";
import ENDPOINTS from "./common/network/Endpoints";

// Production-only diagnostics. No Sentry in dev/staging/local. Used to capture the
// (not-locally-reproducible) empty-client-list anomaly; safe to remove once the
// root cause is confirmed. Set REACT_APP_SENTRY_DSN in the prod build env.
if (
  window.location.hostname === "welcome.mypnoe.com" &&
  process.env.REACT_APP_SENTRY_DSN
) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: "prod",
    // Diagnostic use only — we emit explicit messages, not perf traces.
    tracesSampleRate: 0,
    // Session Replay: don't record normal sessions, only the session in which the
    // client-list anomaly fires (stays within the free 50 replays/month).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      // Healthcare app — mask ALL text and media so patient/client PII never
      // leaves the browser in the replay.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // Replay captures no request/response detail by default. Opt in for the
        // ONE endpoint under investigation: we need to see what GetCustomers
        // actually returns (an empty 200 whose 92-byte body and cache headers we
        // can only infer today). String patterns are substring matches, so this
        // also covers the cache-busting `?_=<ts>` retry. Every other request keeps
        // the default headers-and-bodies-off behaviour.
        networkDetailAllowUrls: [ENDPOINTS.PLATFORM.GET_CUSTOMERS],
        // Beyond Replay's defaults (content-type/content-length/accept).
        // Deliberately NOT Authorization or Cookie — never ship credentials.
        networkRequestHeaders: [
          "cache-control",
          "pragma",
          "x-environment",
          "accept-version",
          "action-source",
        ],
        // Aimed at the open question: is an intermediary serving this, and which
        // backend instance answered?
        networkResponseHeaders: [
          "cache-control",
          "age",
          "expires",
          "etag",
          "date",
          "x-cache",
          "cf-cache-status",
          "via",
          "x-azure-ref",
          "x-amzn-requestid",
          "x-amzn-trace-id",
        ],
        // Safety net for the one way this could leak patient data: replays flush a
        // buffer of the ~60s BEFORE the error, so a successful full-roster
        // GetCustomers in that window would otherwise be captured too (network
        // bodies are not covered by maskAllText). The bodies we actually need are
        // the empty ones (~92 bytes), so drop anything bigger.
        beforeAddRecordingEvent: (event) => {
          const payload = event?.data?.payload;
          if (
            payload?.op !== "resource.xhr" &&
            payload?.op !== "resource.fetch"
          ) {
            return event;
          }
          const MAX_BODY_CHARS = 2000;
          ["request", "response"].forEach((direction) => {
            const part = payload?.data?.[direction];
            if (!part || part.body === undefined || part.body === null) {
              return;
            }
            // Replay JSON-parses bodies before this hook, so `body` is normally an
            // object here — never assume a string. Measure serialized length, and
            // withhold anything we cannot measure rather than guessing.
            let length;
            try {
              length =
                typeof part.body === "string"
                  ? part.body.length
                  : JSON.stringify(part.body).length;
            } catch {
              length = Infinity;
            }
            if (length > MAX_BODY_CHARS) {
              part.body = `[withheld: ${length} chars — non-empty client roster]`;
            }
          });
          return event;
        },
      }),
    ],
    // Scoped: send ONLY the client-list anomaly. Drop Sentry's default app-wide
    // capture of unhandled errors/rejections so the dashboard and the free
    // quota stay reserved for this investigation.
    beforeSend(event) {
      // The anomaly is sent as captureException (message events never trigger
      // on-error Session Replay); keep matching the old message form too.
      const isClientListAnomaly =
        event.message === "client-list-empty" ||
        (event.exception?.values || []).some(
          (v) => v.value === "client-list-empty"
        );
      return isClientListAnomaly ? event : null;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <StyledEngineProvider injectFirst>
    <Provider store={store}>
      <App />
    </Provider>
  </StyledEngineProvider>

  // <React.StrictMode>
  //
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
